import {
    webViewPunchFlowServiceFactory,
    PunchFlowServiceBrowserClient,
    ClockedIn,
    ClockedOut,
    BreakStarted,
    BreakEnded,
    TipsNeeded,
    BrowserAttestationRequired,
    BrowserDepartmentSelectionRequired,
    BrowserHealthCheckRequired,
    BrowserHealthCheckAnswer,
    BrowserRoleSelectionRequired,
    BrowserClockedIn,
    BrowserClockedOut,
    BrowserOnBreak,
    BrowserClockOutAction,
    BrowserEndBreakAction,
    BrowserStartShiftAction,
    BrowserStartUnscheduledShiftAction,
    BrowserStartPaidBreakAction,
    BrowserStartUnpaidBreakAction,
    BrowserStartCustomBreakAction,
    InvalidPunchId,
    BreakTooShort,
    HealthCheckFailed,
    APIError,
    shutdownPunchFlowService
} from 'Shared7Punches';
import type { PunchBrowserResponse, BrowserClockRole } from 'Shared7Punches';
import { env } from '@src/config/env';
import { reportNetworkError } from '@src/context/NetworkStatusContext';
import type {
    PunchResponse,
    ClockRole,
    ErrorType,
    HealthCheckAnswer,
    UserStatusAction,
    StatusPunch,
    StatusShift,
    PreferredShift
} from './punchTypes';
import type { BrowserAvailableAction, BrowserShift } from 'Shared7Punches';
export type {
    PunchResponse,
    SuccessResponse,
    TipsRequiredResponse,
    UserStatusResponse,
    AttestationRequiredResponse,
    DepartmentSelectionRequiredResponse,
    HealthCheckRequiredResponse,
    RoleSelectionRequiredResponse,
    PunchErrorResponse,
    ErrorType,
    Attestation,
    AttestationContent,
    AttestationResponseOption,
    ClockDepartment,
    ClockRole,
    ClockUser,
    PreferredShift,
    StatusPunch,
    StatusShift,
    StatusBreak,
    HealthCheckAnswer,
    HealthCheckQuestionnaire,
    HealthCheckQuestion,
    HealthCheckResponseOption
} from './punchTypes';

// ---- Client params ------------------------------------------------------

export interface ClientParams {
    userId: number;
    companyId: number;
    locationHash: string;
    locationId: number;
}

// ---- Singleton client ---------------------------------------------------

let singletonClient: PunchFlowServiceBrowserClient | null = null;

export function getOrCreateClient(
    params: ClientParams,
    tokenProvider: () => string
): PunchFlowServiceBrowserClient {
    if (!singletonClient) {
        singletonClient = webViewPunchFlowServiceFactory(
            params.userId,
            params.companyId,
            params.locationHash,
            params.locationId,
            `${env.apiBaseUrl}/api`,
            env.osVersion,
            env.appVersion,
            env.debug,
            (error: Error) => console.error('[KMP]', error),
            tokenProvider
        );
    }
    return singletonClient;
}

export function resetClient(): void {
    // Tear down the Koin graph before releasing the singleton, so the next
    // getOrCreateClient() call rebuilds against a clean state (API-4681).
    shutdownPunchFlowService();
    singletonClient = null;
}

// ---- Response mapping ---------------------------------------------------

const ACTION_LABELS: Record<string, string> = {
    clock_out: 'Clock Out',
    end_break: 'End Break',
    start_shift: 'Start shift',
    start_unscheduled_shift: 'Start unscheduled shift',
    start_paid_break: 'Start Paid Break',
    start_unpaid_break: 'Start Break'
};

function mapBrowserActions(
    actions: BrowserAvailableAction[]
): UserStatusAction[] {
    const result: UserStatusAction[] = [];
    for (const action of actions) {
        if (action instanceof BrowserStartShiftAction) {
            result.push({
                type: 'start_shift',
                label: ACTION_LABELS[action.type] ?? action.type,
                execute: () => mapAsync(action.invoke()),
                executeWithRole: (roleId: number) =>
                    mapAsync(action.invokeWithRole(roleId))
            });
        } else if (
            action instanceof BrowserClockOutAction ||
            action instanceof BrowserEndBreakAction ||
            action instanceof BrowserStartUnscheduledShiftAction ||
            action instanceof BrowserStartPaidBreakAction ||
            action instanceof BrowserStartUnpaidBreakAction
        ) {
            result.push({
                label: ACTION_LABELS[action.type] ?? action.type,
                execute: () => mapAsync(action.invoke())
            });
        } else if (action instanceof BrowserStartCustomBreakAction) {
            for (const cb of action.customBreaks) {
                result.push({
                    label: cb.name,
                    execute: () => mapAsync(action.invoke(cb.id))
                });
            }
        }
    }
    const SORT_ORDER: Record<string, number> = {
        'Start shift': 0,
        'Start unscheduled shift': 1,
        'Start Break': 5,
        'Start Paid Break': 4,
        'End Break': 6,
        'Clock Out': 7
    };
    return result.sort(
        (a, b) => (SORT_ORDER[a.label] ?? 3) - (SORT_ORDER[b.label] ?? 3)
    );
}

function mapPunch(punch: {
    clockedInAt: string;
    assignmentRoleName: string | null;
    assignmentDepartmentName: string | null;
}): StatusPunch {
    return {
        clockedInAt: punch.clockedInAt,
        roleName: punch.assignmentRoleName ?? null,
        departmentName: punch.assignmentDepartmentName ?? null
    };
}

// The API omits the leading '#' from hex color strings (e.g. "219EBC").
// Normalise here so downstream components can use the value directly in CSS.
export function normalizeColor(color: string | null | undefined): string | null {
    if (!color) return null;
    return color.startsWith('#') ? color : `#${color}`;
}

function mapShift(shift: BrowserShift): StatusShift {
    return {
        roleName: shift.assignmentRoleName ?? null,
        departmentName: shift.assignmentDepartmentName ?? null,
        color: normalizeColor(shift.assignmentRoleColor),
        startTime: shift.startTime,
        endTime: shift.endTime
    };
}

function mapBrowserRole(role: {
    id: number;
    name: string;
    color?: string | null;
    departmentName?: string | null;
}): ClockRole {
    return {
        id: role.id,
        name: role.name,
        departmentId: null,
        departmentName: role.departmentName ?? null,
        color: normalizeColor(role.color)
    };
}

function mapRole(role: BrowserClockRole): ClockRole {
    return {
        id: role.id,
        name: role.name,
        departmentId: role.departmentId ?? null,
        departmentName: role.departmentName ?? null,
        color: normalizeColor(role.color)
    };
}

async function mapAsync(
    raw: Promise<PunchBrowserResponse | null | undefined>
): Promise<PunchResponse> {
    return processResponse(await raw);
}

function classifyApiError(message: string): ErrorType {
    if (/not assigned to location/i.test(message)) {
        return 'not_assigned_to_location';
    }
    if (/punching in too early is not allowed/i.test(message)) {
        return 'clock_in_too_early';
    }
    if (/aren't scheduled to work today/i.test(message)) {
        return 'shift_not_scheduled';
    }
    if (/\b5\d{2}\b/.test(message) || /internal server error/i.test(message)) {
        return 'server';
    }
    if (
        /failed to fetch|networkerror|network error|net::|eof|timeout/i.test(
            message
        )
    ) {
        return 'network';
    }
    return 'unknown';
}

export function processResponse(
    response: PunchBrowserResponse | null | undefined
): PunchResponse {
    if (response == null) {
        throw new Error('KMP returned null or undefined response');
    }

    if (response instanceof APIError) {
        const errorType = classifyApiError(response.message ?? '');
        // Piggyback network detection: a real punch/KMP API call failing due
        // to a network error transitions the app offline immediately, with
        // no additional request needed (API-4377).
        // TODO: Have concerns this might be too reactive, but will experiment
        if (errorType === 'network') {
            reportNetworkError();
        }
        return {
            type: 'error',
            errorType,
            message: null
        };
    }

    if (response instanceof ClockedIn || response instanceof ClockedOut) {
        return {
            type: 'success',
            punchType: response instanceof ClockedIn ? 'clock_in' : 'clock_out',
            timePunchId: response.timePunchId,
            userPhotoUrl: response.userPhotoUrl ?? null,
            message: response.message ?? null,
            punchedAt: new Date().toISOString()
        };
    }

    if (response instanceof BreakStarted) {
        return {
            type: 'success',
            punchType: 'break_start',
            timePunchId: response.timePunchId,
            userPhotoUrl: response.userPhotoUrl ?? null,
            message: response.message ?? null,
            punchedAt: new Date().toISOString(),
            breakId: response.breakId
        };
    }

    if (response instanceof BreakEnded) {
        return {
            type: 'success',
            punchType: 'break_end',
            timePunchId: response.timePunchId,
            userPhotoUrl: response.userPhotoUrl ?? null,
            message: response.message ?? null,
            punchedAt: new Date().toISOString(),
            breakId: response.breakId,
            breakDurationInMinutes: response.breakDurationInMinutes
        };
    }

    if (response instanceof TipsNeeded) {
        return {
            type: 'tips_required',
            submitTips: (amount: number) => mapAsync(response.sendTips(amount))
        };
    }

    if (response instanceof BrowserAttestationRequired) {
        return {
            type: 'attestation_required',
            attestation: {
                id: response.attestation.id,
                contents: response.attestation.contents.map(c => ({
                    language: c.language,
                    question: c.question,
                    policy: c.policy,
                    buttonText: c.buttonText,
                    responses: c.responses.map(r => ({
                        id: r.id,
                        text: r.text
                    }))
                }))
            },
            sendAttestationResponse: (responseId: number) =>
                mapAsync(response.sendAttestationResponse(responseId))
        };
    }

    if (response instanceof BrowserDepartmentSelectionRequired) {
        return {
            type: 'department_selection_required',
            departments: response.departments.map(d => ({
                id: d.id,
                name: d.name
            })),
            selectDepartment: (id: number) =>
                mapAsync(response.selectDepartment(id))
        };
    }

    if (response instanceof BrowserHealthCheckRequired) {
        return {
            type: 'health_check_required',
            questionnaires: response.questionnaires.map(q => ({
                language: q.language,
                title: q.title,
                buttonText: q.buttonText,
                questions: q.questions.map(qn => ({
                    id: qn.id,
                    text: qn.text,
                    responses: qn.responses.map(r => ({
                        id: r.id,
                        text: r.text
                    }))
                }))
            })),
            submitResponses: (answers: HealthCheckAnswer[]) =>
                mapAsync(
                    response.submitResponses(
                        answers.map(
                            a =>
                                new BrowserHealthCheckAnswer(
                                    a.questionId,
                                    a.responseId
                                )
                        )
                    )
                )
        };
    }

    if (response instanceof BrowserRoleSelectionRequired) {
        return {
            type: 'role_selection_required',
            roles: response.roles.map(mapRole),
            selectRole: (id: number) => mapAsync(response.selectRole(id))
        };
    }

    if (response instanceof BrowserClockedIn) {
        return {
            type: 'user_status',
            status: 'clocked_in',
            user: {
                id: response.user.id,
                firstName: response.user.firstName,
                lastName: response.user.lastName,
                profilePictureUrl: response.user.profilePictureUrl
            },
            availableActions: mapBrowserActions(response.availableActions),
            serverTime: response.serverTime,
            punch: mapPunch(response.punch),
            currentShift: response.currentShift
                ? mapShift(response.currentShift)
                : null
        };
    }

    if (response instanceof BrowserClockedOut) {
        const ps = response.preferredShift;
        const preferredShift: PreferredShift | null = ps
            ? {
                  shift: mapShift(ps.shift),
                  otherAssignedRoles: (ps.otherAssignedRoles ?? []).map(
                      mapBrowserRole
                  )
              }
            : null;
        return {
            type: 'user_status',
            status: 'clocked_out',
            user: {
                id: response.user.id,
                firstName: response.user.firstName,
                lastName: response.user.lastName,
                profilePictureUrl: response.user.profilePictureUrl
            },
            availableActions: mapBrowserActions(response.availableActions),
            serverTime: response.serverTime,
            preferredShift
        };
    }

    if (response instanceof BrowserOnBreak) {
        const cb = response.currentBreak;
        return {
            type: 'user_status',
            status: 'on_break',
            user: {
                id: response.user.id,
                firstName: response.user.firstName,
                lastName: response.user.lastName,
                profilePictureUrl: response.user.profilePictureUrl
            },
            availableActions: mapBrowserActions(response.availableActions),
            serverTime: response.serverTime,
            punch: mapPunch(response.punch),
            currentShift: response.currentShift
                ? mapShift(response.currentShift)
                : null,
            currentBreak: {
                displayLabel:
                    cb.type === 'custom' && cb.customBreakName
                        ? cb.customBreakName
                        : 'Break',
                startTime: cb.startTime,
                expectedEndTime: cb.expectedEndBreakTime ?? null,
                minLengthMinutes: cb.customBreakMinLengthInMinutes ?? null
            }
        };
    }

    if (response instanceof InvalidPunchId) {
        return {
            type: 'error',
            errorType: 'invalid_punch_id',
            message: response.message ?? null
        };
    }

    if (response instanceof BreakTooShort) {
        return {
            type: 'error',
            errorType: 'break_too_short',
            message: response.message ?? null
        };
    }

    if (response instanceof HealthCheckFailed) {
        return {
            type: 'error',
            errorType: 'health_check_failed',
            message: response.message ?? null
        };
    }

    throw new Error(
        `Unrecognized KMP response: ${
            Object.getPrototypeOf(response).constructor.name
        }`
    );
}
